import {useCallback, useMemo} from 'react';
import {useLocalStorage} from 'usehooks-ts';

import {stringToHash} from '~utils/miscUtils';
import {selectUserEmail} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {DONT_SHOW_AGAIN_KEYS} from 'src/constants/constants';

interface DontShowAgainObject {
  user: string;
  key: string;
}

export function useDontShowAgain(key: string | undefined) {
  const [localStorageDSA, setLocalStorageDSA] = useLocalStorage<DontShowAgainObject[]>(DONT_SHOW_AGAIN_KEYS, []);
  const userEmail = useAppSelector(selectUserEmail);

  const emailHash = useMemo(() => {
    if (!userEmail || !key) return undefined;
    return stringToHash(userEmail);
  }, [key, userEmail]);

  const shouldShow = useMemo(() => {
    if (!key) return true;
    if (!emailHash) return true;
    return !localStorageDSA.some((object) => object.user === emailHash && object.key === key);
  }, [emailHash, key, localStorageDSA]);

  const dismiss = useCallback(
    (opts?: {delayMs?: number}) => {
      if (!key || !emailHash) return;
      if (localStorageDSA.some((object) => object.user === emailHash && object.key === key)) return;

      const delayMs = opts?.delayMs ?? 0;
      if (delayMs <= 0) {
        setLocalStorageDSA([...localStorageDSA, {user: emailHash, key}]);
        return;
      }

      setTimeout(() => {
        setLocalStorageDSA((prev) => {
          if (prev.some((object) => object.user === emailHash && object.key === key)) return prev;
          return [...prev, {user: emailHash, key}];
        });
      }, delayMs);
    },
    [emailHash, key, localStorageDSA, setLocalStorageDSA],
  );

  return {dismiss, shouldShow};
}
